import React, { useState, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronDown, ArrowUp, ArrowDown, ChevronsUpDown } from 'lucide-react';
import { TeamMember, Status, PaymentMethod } from '../types';
import { StatusPill } from './StatusPill';
import { PaymentMethodPill } from './PaymentMethodPill';
import { ExpandedRow } from './ExpandedRow';
import { formatCurrency } from '../utils/formatting';
import { toast } from 'sonner';
interface TeamMemberTableProps {
  members: TeamMember[];
  onUpdateStatus: (memberId: string, status: Status) => void;
  onUpdatePaymentMethod: (memberId: string, method: PaymentMethod) => void;
  sortBy: string | null;
  sortDirection: 'asc' | 'desc';
  onSort: (column: string) => void;
}
export function TeamMemberTable({
  members,
  onUpdateStatus,
  onUpdatePaymentMethod,
  sortBy,
  sortDirection,
  onSort
}: TeamMemberTableProps) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [openStatusDropdown, setOpenStatusDropdown] = useState<string | null>(null);
  const [openPaymentDropdown, setOpenPaymentDropdown] = useState<string | null>(null);
  const [savingStatus, setSavingStatus] = useState<string | null>(null);
  const [savingPayment, setSavingPayment] = useState<string | null>(null);
  const allExpanded = members.length > 0 && expandedIds.size === members.length;
  const handleRowClick = (memberId: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(memberId)) {
        next.delete(memberId);
      } else {
        next.add(memberId);
      }
      return next;
    });
    setOpenStatusDropdown(null);
    setOpenPaymentDropdown(null);
  };
  const toggleAll = () => {
    if (allExpanded) {
      setExpandedIds(new Set());
    } else {
      setExpandedIds(new Set(members.map(m => m.id)));
    }
  };
  const handleStatusSelect = async (memberId: string, status: Status) => {
    setSavingStatus(memberId);
    setOpenStatusDropdown(null);
    await new Promise(resolve => setTimeout(resolve, 600));
    onUpdateStatus(memberId, status);
    setSavingStatus(null);
    toast.success('Status updated successfully');
  };
  const handlePaymentMethodSelect = async (memberId: string, method: PaymentMethod) => {
    setSavingPayment(memberId);
    setOpenPaymentDropdown(null);
    await new Promise(resolve => setTimeout(resolve, 600));
    onUpdatePaymentMethod(memberId, method);
    setSavingPayment(null);
    toast.success('Payment method updated successfully');
  };
  const SortIcon = ({
    column
  }: {
    column: string;
  }) => {
    if (sortBy !== column) {
      return <ArrowUp className="w-4 h-4 text-slate-300" data-id="element-269" />;
    }
    return sortDirection === 'asc' ? <ArrowUp className="w-4 h-4 text-blue-600" data-id="element-270" /> : <ArrowDown className="w-4 h-4 text-blue-600" data-id="element-271" />;
  };
  if (members.length === 0) {
    return <div className="bg-white rounded-xl border border-slate-200 p-12 text-center" data-id="element-272">
        <div className="text-slate-400 mb-2" data-id="element-273">
          <svg className="w-16 h-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-274">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" data-id="element-275" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-slate-900 mb-1" data-id="element-276">
          No records found
        </h3>
        <p className="text-slate-500" data-id="element-277">
          Try adjusting your filters to see more results
        </p>
      </div>;
  }
  return <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-id="element-278">
      <div className="flex items-center justify-between px-6 py-3 border-b border-slate-200 bg-white" data-id="element-279">
        <div className="text-sm text-slate-600" data-id="element-280">
          Showing{' '}
          <span className="font-semibold text-slate-900" data-id="element-281">{members.length}</span>{' '}
          {members.length === 1 ? 'employee' : 'employees'}
        </div>
        <button onClick={toggleAll} className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors" data-id="element-282">
          <ChevronsUpDown className="w-4 h-4" data-id="element-283" />
          {allExpanded ? 'Collapse all' : 'Expand all'}
        </button>
      </div>
      <div className="overflow-x-auto" data-id="element-284">
        <table className="w-full" data-id="element-285">
          <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10" data-id="element-286">
            <tr data-id="element-287">
              <th className="px-6 py-3 text-left" data-id="element-288">
                <button onClick={() => onSort('name')} className="flex items-center gap-2 font-semibold text-slate-700 hover:text-slate-900 text-sm" data-id="element-289">
                  MLO Name
                  <SortIcon column="name" data-id="element-290" />
                </button>
              </th>
              <th className="px-6 py-3 text-left" data-id="element-291">
                <button onClick={() => onSort('paymentMethod')} className="flex items-center gap-2 font-semibold text-slate-700 hover:text-slate-900 text-sm" data-id="element-292">
                  Preferred Payment Method
                  <SortIcon column="paymentMethod" data-id="element-293" />
                </button>
              </th>
              <th className="px-6 py-3 text-left" data-id="element-294">
                <button onClick={() => onSort('status')} className="flex items-center gap-2 font-semibold text-slate-700 hover:text-slate-900 text-sm" data-id="element-295">
                  Status
                  <SortIcon column="status" data-id="element-296" />
                </button>
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-slate-700" data-id="element-297">
                Pay Period
              </th>
              <th className="px-6 py-3 text-right" data-id="element-298">
                <button onClick={() => onSort('payroll')} className="flex items-center gap-2 font-semibold text-slate-700 hover:text-slate-900 text-sm ml-auto" data-id="element-299">
                  Total Payroll Amount
                  <SortIcon column="payroll" data-id="element-300" />
                </button>
              </th>
            </tr>
          </thead>
          <tbody data-id="element-301">
            {members.map(member => {
            const isExpanded = expandedIds.has(member.id);
            return <Fragment key={member.id} data-id="element-302">
                  <motion.tr initial={false} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors" onClick={() => handleRowClick(member.id)} data-id="element-303">
                    <td className="px-6 py-4" data-id="element-304">
                      <div className="flex items-center gap-3" data-id="element-305">
                        {isExpanded ? <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" data-id="element-306" /> : <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" data-id="element-307" />}
                        <div className="flex items-center gap-2" data-id="element-308">
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-semibold" data-id="element-309">
                            {member.paymentItemCount}
                          </span>
                          <span className="font-medium text-slate-900" data-id="element-310">
                            {member.name}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4" onClick={e => e.stopPropagation()} data-id="element-311">
                      <PaymentMethodPill method={member.preferredPaymentMethod} isOpen={openPaymentDropdown === member.id} isSaving={savingPayment === member.id} onToggle={() => setOpenPaymentDropdown(openPaymentDropdown === member.id ? null : member.id)} onSelect={method => handlePaymentMethodSelect(member.id, method)} data-id="element-312" />
                    </td>
                    <td className="px-6 py-4" onClick={e => e.stopPropagation()} data-id="element-313">
                      <StatusPill status={member.status} isOpen={openStatusDropdown === member.id} isSaving={savingStatus === member.id} onToggle={() => setOpenStatusDropdown(openStatusDropdown === member.id ? null : member.id)} onSelect={status => handleStatusSelect(member.id, status)} data-id="element-314" />
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600" data-id="element-315">
                      {member.payPeriod}
                    </td>
                    <td className="px-6 py-4 text-right font-semibold text-slate-900" data-id="element-316">
                      {formatCurrency(member.totalPayrollAmount)}
                    </td>
                  </motion.tr>
                  <AnimatePresence data-id="element-317">
                    {isExpanded && <motion.tr initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} exit={{
                  opacity: 0
                }} transition={{
                  duration: 0.15
                }} data-id="element-318">
                        <td colSpan={5} className="p-0" data-id="element-319">
                          <ExpandedRow payPeriods={member.payPeriods} commissionModel={member.commissionModel} data-id="element-320" />
                        </td>
                      </motion.tr>}
                  </AnimatePresence>
                </Fragment>;
          })}
          </tbody>
        </table>
      </div>
    </div>;
}